import { User, multiFactor } from 'firebase/auth';
import { doc, getDoc } from 'firebase/firestore';
import { db } from '../firebase';

export type GateReason = 'unauthenticated' | 'not_super_admin' | 'mfa_required' | 'ok';

export interface GateResult {
  allowed: boolean;
  reason: GateReason;
}

const MFA_ENABLED = import.meta.env.VITE_CONSOLE_ENABLE_MFA === 'true';

// Cache module-level per il fallback Firestore `is_super_admin`.
// Si resetta al refresh pagina. Se un utente viene promosso super_admin
// durante la sessione, deve fare logout/login per rivedere il claim.
const superAdminCache = new Map<string, boolean>();

export async function gateConsoleAccess(user: User | null): Promise<GateResult> {
  if (!user) return { allowed: false, reason: 'unauthenticated' };

  try {
    // Force refresh del token (true) per assicurare che i custom claims piu'
    // recenti siano nel JWT. Senza questo, un super_admin appena promosso
    // (claim settato via admin SDK) vedrebbe ancora il vecchio token cachato
    // e fallirebbe il gating fino alla scadenza naturale del token (~1h).
    const tokenResult = await user.getIdTokenResult(true);
    let isSA = tokenResult.claims.is_super_admin === true;

    if (!isSA) {
      if (superAdminCache.has(user.uid)) {
        isSA = superAdminCache.get(user.uid)!;
      } else {
        try {
          const userDoc = await getDoc(doc(db, 'app_users', user.uid));
          isSA = userDoc.exists() && userDoc.data()?.is_super_admin === true;
          superAdminCache.set(user.uid, isSA);
        } catch {
          // PERMISSION_DENIED previsto per non-super-admin:
          // cachiamo false per non riprovare ad ogni gate nella stessa sessione.
          superAdminCache.set(user.uid, false);
          isSA = false;
        }
      }
    }

    if (!isSA) return { allowed: false, reason: 'not_super_admin' };

    if (MFA_ENABLED) {
      const mfaEnrolled = multiFactor(user).enrolledFactors.length;
      if (mfaEnrolled === 0) return { allowed: false, reason: 'mfa_required' };
    }

    return { allowed: true, reason: 'ok' };
  } catch (error) {
    console.error('gateConsoleAccess failed', error);
    return { allowed: false, reason: 'unauthenticated' };
  }
}
